import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import NoteTab from "./NoteTab";
import TranscriptTab from "./TranscriptTab";
import TemplateDropdown from "./TemplateDropdown";
import { usePDF } from "@context/PDFContext";
import { useError } from "@context/ErrorContext";
import { formJsonToText } from "@utils/formJsonToText";
import EditFilePickerModal from "./EditFilePickerModal";
import { ConsultationService } from "../../services/consultationService";
import RecordToggleButton from "./RecordToggleButton";

export default function ConsultationDetails({
  consultation,
  filesForRecording = [],
  consultationManager,
  onStartRecording,
  onStopRecording,
  isContinuingRecording,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("note");
  const [copied, setCopied] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const [showEditPicker, setShowEditPicker] = useState(false);
  const [showExportPicker, setShowExportPicker] = useState(false);
  const { handleExportPDF } = usePDF();
  const { showError } = useError();

  // Template state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // Debug prop changes to track timestamp modifications
  useEffect(() => {
    console.log("🏥 CONSULTATION PROP CHANGED:");
    console.log("New consultation:", consultation);
    console.log("New filesForRecording:", filesForRecording);
    if (consultation) {
      console.log("Consultation timestamps:", {
        local_datetime: consultation.local_datetime,
        created_at: consultation.created_at,
        recording_id: consultation.recording_id,
      });
    }
  }, [consultation, filesForRecording]);

  const consultationService = useMemo(
    () => new ConsultationService(process.env.REACT_APP_BACKEND_BASE_URL || ""),
    []
  );

  // Use siblings if provided; otherwise just the single consultation
  const files = filesForRecording.length
    ? filesForRecording
    : consultation
    ? [consultation]
    : [];

  // Get already generated template IDs from existing files
  const generatedTemplateIds = useMemo(() => {
    const templateIds = new Set();
    files.forEach((file) => {
      if (file.template_id) {
        templateIds.add(String(file.template_id));
      }
    });
    return Array.from(templateIds);
  }, [files]);

  const recordingId =
    consultation?.recording_id ||
    consultation?.session_id ||
    consultation?.file_id ||
    consultation?.id ||
    null;

  // Load templates on mount
  useEffect(() => {
    let mounted = true;
    const loadTemplates = async () => {
      try {
        console.log("🔄 Loading templates...");
        setLoadingTemplates(true);
        const templatesData = await consultationService.fetchTemplates();
        console.log("📋 Templates loaded:", templatesData);
        if (mounted) setTemplates(templatesData || []);
      } catch (err) {
        console.error("❌ Failed to load templates", err);
        showError?.("Failed to load templates");
      } finally {
        if (mounted) setLoadingTemplates(false);
      }
    };
    loadTemplates();
    return () => {
      mounted = false;
    };
  }, [consultationService, showError]);

  // Handle template selection
  const handleToggleTemplate = (templateId) => {
    const idStr = String(templateId);
    setSelectedTemplateIds((prev) =>
      prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]
    );
  };

  // Handle generate button - follows same pattern as Finish button
  const handleGenerate = async () => {
    if (!selectedTemplateIds.length || !recordingId) {
      showError?.("No templates selected or recording ID available");
      return;
    }

    if (!consultationManager) {
      showError?.("Consultation manager not available");
      return;
    }

    // Generate a unique session ID for this generation process
    const sessionId = `generate-${recordingId}-${Date.now()}`;

    try {
      setIsGenerating(true);

      // Add processing placeholder
      consultationManager.addProcessingPlaceholder(
        sessionId,
        new Date().toISOString()
      );

      // Perform the generation in the background
      const transcription = (files[0] || consultation)?.transcript || "";
      const base = files[0] || consultation || {};
      const existingDateTime =
        base.local_datetime ||
        base.file_name ||
        consultation?.local_datetime ||
        consultation?.file_name;
      const localDateTime =
        existingDateTime ||
        consultationManager?.formatLocalDateTime?.() ||
        new Date().toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

      console.log("📤 SENDING TO BACKEND:");
      console.log("recordingId:", recordingId);
      console.log("localDateTime being sent:", localDateTime);
      console.log("selectedTemplateIds:", selectedTemplateIds);

      const result = await consultationService.autofillSelectedTemplates(
        selectedTemplateIds,
        recordingId,
        transcription,
        localDateTime
      );

      console.log("📥 BACKEND RESPONSE:", result);

      let fileIds =
        result?.data?.fileIds ||
        (result?.data?.fileId ? [result.data.fileId] : []);

      // Handle async task polling if needed
      if (
        !fileIds?.length &&
        (result?.data?.task_id || result?.data?.status_url)
      ) {
        const finalResult = await consultationService.pollAutofillStatus({
          jobId: result.data.task_id,
          statusUrl: result.data.status_url,
        });
        fileIds = finalResult?.fileIds || [];
      }

      // Complete the process (same as Finish button)
      if (fileIds.length) {
        consultationManager.removeProcessingPlaceholder(sessionId);

        // Refresh consultations to get the newly generated files
        await consultationManager.fetchConsultations();

        // Clear selected templates
        setSelectedTemplateIds([]);
      } else {
        consultationManager.removeProcessingPlaceholder(sessionId);
        showError?.("No files were generated");
      }
    } catch (err) {
      // Remove placeholder on error
      consultationManager.removeProcessingPlaceholder(sessionId);
      showError?.(err.message || "Failed to generate templates");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePickToEdit = (f) => {
    setShowEditPicker(false);
    if (!f) return;
    navigate("/fill-form", {
      state: {
        id: f.file_id,
        name: f.file_name,
        formName: f.template_name,
        content: f.file_content,
        template_id: f.template_id,
      },
    });
  };

  const handlePickToExport = async (f) => {
    setShowExportPicker(false);
    if (!f) return;
    const title = `${f.template_name}_${f.file_name}`;
    try {
      await handleExport(f.file_id, title, f.date);
    } catch (e) {
      console.error(e);
      showError?.("Failed to export PDF.");
    }
  };

  const handleExport = async (fileId, title, date) => {
    try {
      await handleExportPDF(fileId, title, date);
    } catch (error) {
      console.error(`Error exporting PDF: ${error.message}`);
    }
  };

  const buildCombinedNoteText = () =>
    files
      .map((f, idx) => {
        const text = f?.file_content
          ? formJsonToText(JSON.parse(f.file_content))
          : "";
        if (!text) return "";
        const header =
          files.length > 1
            ? `--- ${f.template_name || f.file_name || `Note ${idx + 1}`} ---\n`
            : "";
        return `${header}${text}`;
      })
      .filter(Boolean)
      .join(`\n\n────────────\n\n`);

  const copyToClipboard = async () => {
    if (!files.length) return;

    let textToCopy = "";
    if (activeTab === "note") {
      textToCopy = buildCombinedNoteText();
    } else if (activeTab === "transcript") {
      // Transcript is shared for the recording; show from the first file or fallback to consultation
      const base = files[0] || consultation;
      textToCopy = base?.transcript || "";
    }

    if (textToCopy) {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  return (
    <div className="flex flex-col w-full h-full px-4 sm:px-8 pt-4 sm:pt-6 min-h-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        {/* Tabs and Template Dropdown Row */}
        <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-6 overflow-x-auto">
          {/* Tabs */}
          <div className="flex gap-2 sm:gap-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("note")}
              className={`py-2 px-3 sm:px-4 font-medium text-sm sm:text-base whitespace-nowrap ${
                activeTab === "note"
                  ? "border-b-2 border-black text-black"
                  : "text-gray-500 hover:text-black"
              }`}
            >
              Note
            </button>
            <button
              onClick={() => setActiveTab("transcript")}
              className={`py-2 px-3 sm:px-4 font-medium text-sm sm:text-base whitespace-nowrap ${
                activeTab === "transcript"
                  ? "border-b-2 border-black text-black"
                  : "text-gray-500 hover:text-black"
              }`}
            >
              Transcript
            </button>
          </div>

          {/* Template Dropdown - hidden on mobile, shown on larger screens */}
          <div className="hidden sm:block relative">
            <TemplateDropdown
              templates={templates}
              selectedTemplateIds={selectedTemplateIds}
              onToggleTemplate={handleToggleTemplate}
              onGenerate={handleGenerate}
              loading={loadingTemplates || isGenerating}
              disabled={!recordingId || isGenerating}
              generatedTemplateIds={generatedTemplateIds}
            />
          </div>
        </div>

        {/* Actions Row - Always visible */}
        <div className="flex items-center justify-between sm:justify-end gap-3">
          {/* Template Dropdown - Only visible on mobile */}
          <div className="sm:hidden relative">
            <TemplateDropdown
              templates={templates}
              selectedTemplateIds={selectedTemplateIds}
              onToggleTemplate={handleToggleTemplate}
              onGenerate={handleGenerate}
              loading={loadingTemplates || isGenerating}
              disabled={!recordingId || isGenerating}
              generatedTemplateIds={generatedTemplateIds}
            />
          </div>

          {/* Action Icons */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Start/Stop continue same session */}
            {recordingId && (
              <RecordToggleButton
                isRecording={!!isContinuingRecording}
                disabled={false}
                title={
                  isContinuingRecording
                    ? "Stop recording"
                    : "Continue recording"
                }
                onStart={() =>
                  onStartRecording?.(recordingId, consultation?.date)
                }
                onStop={() => onStopRecording?.()}
              />
            )}
            <button
              className="text-gray-500 hover:text-black p-1"
              title="Copy"
              onClick={copyToClipboard}
            >
              {copied ? (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinejoin="round"
                    d="M14 4v3a1 1 0 0 1-1 1h-3m4 10v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h2m11-3v10a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V7.87a1 1 0 0 1 .24-.65l2.46-2.87a1 1 0 0 1 .76-.35H18a1 1 0 0 1 1 1Z"
                  />
                </svg>
              )}
            </button>

            {activeTab === "note" && files.length > 0 && (
              <>
                <button
                  className="text-gray-500 hover:text-black p-1"
                  title="Edit Note"
                  onClick={() => setShowEditPicker(true)}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 5H6a2 2 0 0 0-2 2v11c0 1.104.896 2 2 2h11a2 2 0 0 0 2-2v-5M18.414 2.586a2 2 0 0 1 2.828 2.828L12 14l-4 1 1-4 9.414-9.414Z"
                    />
                  </svg>
                </button>

                {/* Print first file (quick action) */}
                <button
                  className="text-gray-500 hover:text-black"
                  onClick={() => setShowExportPicker(true)}
                  title="Export as PDF"
                  disabled={printingAll}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinejoin="round"
                      d="M16.444 18H19a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2.556M17 11V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v6h10ZM7 15h10v4a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-4Z"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap pr-2 overflow-y-auto scrollbar-hide">
        {activeTab === "note" ? (
          <div>
            {files.map((f, idx) => {
              const noteText = f?.file_content
                ? formJsonToText(JSON.parse(f.file_content))
                : "";
              return (
                <section key={f.file_id || idx} className="mb-6">
                  {files.length > 1 && (
                    <div className="text-xs text-gray-500 mb-2">
                      {f.template_name || f.file_name}
                    </div>
                  )}
                  <NoteTab consultation={noteText} />
                  {idx < files.length - 1 && (
                    <hr className="my-6 border-gray-300 border-dashed" />
                  )}
                </section>
              );
            })}
          </div>
        ) : (
          // Show the shared transcript once
          <TranscriptTab consultation={files[0] || consultation} />
        )}
      </div>
      <EditFilePickerModal
        open={showEditPicker}
        onClose={() => setShowEditPicker(false)}
        files={files}
        onSelect={handlePickToEdit}
      />
      <EditFilePickerModal
        open={showExportPicker}
        onClose={() => setShowExportPicker(false)}
        files={files}
        onSelect={handlePickToExport}
        title="Choose a file to export"
      />
    </div>
  );
}
